import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { Button, Page, PageContentHeader, Spinner, TableList } from 'seagull';
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import { routes } from "../../../constants";
import { useIoContext, useStore, useStoreActions } from "../../../contexts";
import { useOnMount } from "../../../hooks/useOnMount";
import { useSetDocumentTitle } from "../../../hooks/useSetDocumentTitle";
import { useSortFromUrl } from "../../../hooks/useSortFromUrl";
import { selectors } from "../../../store/state-selectors";
import TableFilters from "../components/TableFilters/TableFilters";
import FutureInvoiceRow from "./FutureInvoiceRow";
import styles from "./FutureInvoices.module.scss";
import { futureInvoicesReportColumnDefs, getDefaultSort, headerItems, makeCsv, makeFilterConfig } from "./FutureInvoices.utils";
import { FilterType, filterLabels } from "./types";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const testIdPrefix = 'future-invoices-report';
const title = 'Future Invoices Report';
export const FutureInvoicesReport = () => {
  const history = useHistory();
  const [futureInvoicesReportData, setFutureInvoicesReportData] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const {
    downloadCsv,
    getNow
  } = useIoContext();
  const sortIds = compact(headerItems.map(item => item.sortId));
  const defaultSort = useMemo(() => getDefaultSort(sortIds), [sortIds]);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const {
    api
  } = useStoreActions();
  const businessId = useStore(selectors.user.businessId);
  const handleFetchFilterConfig = async () => {
    const [companyNames, agreementNames] = await Promise.all([api.reports.getFutureInvoicesCompanyNames(), api.reports.getFutureInvoicesAgreementNames()]);
    const filterOptions = {
      [FilterType.CompanyName]: companyNames,
      [FilterType.AgreementName]: agreementNames
    };
    return makeFilterConfig(filterOptions, getNow());
  };
  const fetchFutureInvoicesReport = (selectedFilters, sort) => {
    var _selectedFilters$Filt, _selectedFilters$Filt2, _selectedFilters$Filt3, _selectedFilters$Filt4;
    if (businessId == null) return;
    setIsLoading(true);
    api.reports.getFutureInvoicesReport({
      companies: selectedFilters[FilterType.CompanyName],
      agreements: selectedFilters[FilterType.AgreementName],
      requiresApproval: selectedFilters[FilterType.RequiresApproval],
      issueDateFrom: (_selectedFilters$Filt = selectedFilters[FilterType.IssueDate]) === null || _selectedFilters$Filt === void 0 ? void 0 : _selectedFilters$Filt.from,
      issueDateTo: (_selectedFilters$Filt2 = selectedFilters[FilterType.IssueDate]) === null || _selectedFilters$Filt2 === void 0 ? void 0 : _selectedFilters$Filt2.to,
      dueDateFrom: (_selectedFilters$Filt3 = selectedFilters[FilterType.DueDate]) === null || _selectedFilters$Filt3 === void 0 ? void 0 : _selectedFilters$Filt3.from,
      dueDateTo: (_selectedFilters$Filt4 = selectedFilters[FilterType.DueDate]) === null || _selectedFilters$Filt4 === void 0 ? void 0 : _selectedFilters$Filt4.to,
      sortField: sort ? sortIds[sort.colIndex] : undefined,
      sortDirection: sort === null || sort === void 0 ? void 0 : sort.sort
    }).then(setFutureInvoicesReportData).finally(() => setIsLoading(false));
  };
  useSetDocumentTitle(title);
  useOnMount(() => {
    fetchFutureInvoicesReport(selectedFilters, sort);
  });
  const onExport = () => {
    downloadCsv(makeCsv(futureInvoicesReportData), 'future-invoices.csv');
  };
  const onSortChange = sort => {
    changeSort(sort);
    fetchFutureInvoicesReport(selectedFilters, sort);
  };
  return /*#__PURE__*/_jsx(Page, {
    testId: testIdPrefix,
    header: title,
    variant: "containedHeight",
    onClickBack: () => history.push(routes.reports),
    actions: /*#__PURE__*/_jsx(Button, {
      testId: "".concat(testIdPrefix, "-export-btn"),
      disabled: isLoading,
      onClick: onExport,
      children: "Export"
    }),
    children: /*#__PURE__*/_jsxs(TableList.Provider, {
      columnDefs: futureInvoicesReportColumnDefs,
      numRows: futureInvoicesReportData.length,
      sort: sort,
      onSortChange: onSortChange,
      children: [/*#__PURE__*/_jsx(PageContentHeader, {
        children: /*#__PURE__*/_jsx("div", {
          className: styles.pageContent,
          children: /*#__PURE__*/_jsx(RestfulWrapper, {
            fetch: handleFetchFilterConfig,
            renderLoader: () => /*#__PURE__*/_jsx(Spinner, {
              size: 24
            }),
            renderChild: _ref => {
              let {
                data: filterConfig
              } = _ref;
              return /*#__PURE__*/_jsx(TableFilters, {
                testIdPrefix: testIdPrefix,
                filterLabels: filterLabels,
                filterConfig: filterConfig,
                selectedFilters: selectedFilters,
                setSelectedFilters: setSelectedFilters,
                onRefresh: fetchFutureInvoicesReport
              });
            }
          })
        })
      }), /*#__PURE__*/_jsx(TableList.Structure, {
        header: /*#__PURE__*/_jsx(TableList.Header, {
          stickyToTop: true,
          items: headerItems,
          testId: "".concat(testIdPrefix, "-table-header")
        }),
        content: isLoading ? /*#__PURE__*/_jsx(Spinner, {
          centeredInBox: true
        }) : /*#__PURE__*/_jsx(_Fragment, {
          children: futureInvoicesReportData.map((futureInvoice, index) => /*#__PURE__*/_jsx(FutureInvoiceRow, {
            futureInvoice: futureInvoice,
            testId: "".concat(testIdPrefix, "-table-row-").concat(index)
          }, futureInvoice.compoundId()))
        })
      })]
    })
  });
};